"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Calendar as CalendarIcon, Loader } from 'lucide-react';
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function JourneyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [dateOpen, setDateOpen] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border rounded-md bg-secondary">
        <p className="font-default text-2xl text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="full_name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-industrial-muted">
              [ Full Name ]
            </FormLabel>
            <FormControl>
              <Input placeholder="Joe Smith" className="border-0 border-b-2 border-industrial-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-industrial-muted/60 font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-industrial-muted">
              [ Email Address ]
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="joe@haulitall.com" className="border-0 border-b-2 border-industrial-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-industrial-muted/60 font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone is required"
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-industrial-muted">
              [ Phone Number ]
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(817) 291-3051" className="border-0 border-b-2 border-industrial-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-industrial-muted/60 font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem className="gap-2 relative">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-industrial-muted">
              [ Preferred Haul Date ]
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Button type="button" variant="ghost" onClick={() => setDateOpen(!dateOpen)} className="w-full justify-between border-0 border-b-2 border-industrial-muted bg-transparent px-0 h-11 hover:bg-transparent text-foreground font-default text-lg">
                  <span className={field.value ? "text-foreground" : "text-industrial-muted/60"}>
                    {field.value ? new Date(field.value).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
              }) : "Select a date"}
                  </span>
                  <SafeHydrate><CalendarIcon className="size-4 text-primary" /></SafeHydrate>
                </Button>
                {dateOpen && <div className="absolute z-50 mt-2 bg-popover border border-border rounded-md shadow-lg">
                    <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                field.onChange(date ? date.toISOString() : "");
                setDateOpen(false);
              }} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} /></SafeHydrate>
                  </div>}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="health_concerns" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-industrial-muted">
              [ Type of Haul ]
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <SelectTrigger className="w-full border-0 border-b-2 border-industrial-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg [&>span]:text-foreground data-[placeholder]:[&>span]:text-industrial-muted/60">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="Residential Junk Removal">
                    Residential Junk Removal
                  </SelectItem>
                  <SelectItem value="Commercial Cleanouts">
                    Commercial Cleanouts
                  </SelectItem>
                  <SelectItem value="Construction Debris">Construction Debris</SelectItem>
                  <SelectItem value="Appliance & Furniture Disposal">Appliance & Furniture Disposal</SelectItem>
                  <SelectItem value="Dumpster Rental Alternative">
                    Dumpster Rental Alternative
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/30 rounded-md bg-destructive/5">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-widest shadow-md transition-transform hover:-translate-y-1">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Confirm My Haul
            <ArrowRight className="size-4 ml-2" />
          </>}
      </Button>
    </Form>;
}
export default function ScheduleYourHaul() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3405" id="schedule-your-haul" className="relative bg-background overflow-hidden dark">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {}
        <div ref={leftRef} className={`relative lg:col-span-6 min-h-[60vh] lg:min-h-screen overflow-hidden transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695455/img/junk_removal_truck_action.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695455/img/junk_removal_truck_action.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695455/img/junk_removal_truck_action.png-wvc-sizes" alt="Joe and Gio Haul It All truck at a dump location" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" src="https://loving-tarpon.10web.cloud/wp-content/uploads/2026/06/IMG_1509.jpg" />
          <div className="absolute inset-0 bg-gradient-to-t from-asphalt-deep/80 via-transparent to-transparent" />

          <div className="absolute bottom-10 left-10 sm:bottom-16 sm:left-16 right-10 max-w-md">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-4">
              [ 06 // THE FINAL STEP ]
            </p>
            <p className="font-default font-bold text-2xl sm:text-3xl text-foreground leading-snug">
              Clear the clutter. Reclaim your space today.
            </p>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-6 bg-card flex items-center transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <div className="w-full px-8 py-20 sm:px-12 sm:py-24 lg:px-20 lg:py-32 max-w-2xl mx-auto">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-6">
              [ SERVICE REQUEST ]
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] mb-8 tracking-tighter">
              Schedule your heavy-duty haul.
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-14 max-w-lg">
              Provide your details and Joe or Gio will contact you within the hour 
              to provide a firm quote and confirm your pickup time. Fast, reliable, 
              and professional junk removal.
            </p>

            <FormProvider formKey="schedule_haul_form_home" formId="schedule_haul_form_home" sectionName="schedule-your-haul" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            preferred_date: "",
            health_concerns: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            }
          }} submitText="Confirm My Haul" successMessage="Request received. Joe or Gio will call you shortly to finalize the details." errorMessage="Submission failed. Please call Joe at (817) 291-3051 directly.">
              <JourneyForm />
            </FormProvider>

            <p className="mt-10 text-sm text-industrial-muted font-mono">DIRECT DISPATCH: <a className="underline decoration-primary underline-offset-4 text-foreground hover:text-primary transition-colors" data-ast-id="f:/src/pages/Home/sections/ScheduleYourHaul.tsx|sig:q8l1lw" data-ast-start="11398" data-ast-end="11592" href="tel:+18172913051">(817) 291-3051</a>&nbsp;OR 817-420-0631</p>
          </div>
        </div>
      </div>
    </section>;
}